import { lazy } from "react";
import { Navigate } from "react-router-dom";

import ProtectedRoute from "../components/auth/ProtectedRoute";
import PublicRoute from "../components/auth/PublicRoute";
import AddPaymentCardDetails from "../components/auth/registration/AddPaymentCardDetails";
import AddTapCardDetails from "../components/auth/registration/AddTapCardDetails";
import InspectionDetails from "../components/jewellery-inspector/manufacture-requests/InspectionDetails";
import AppLayout from "../components/layouts/AppLayout";
import AuthLayout from "../components/layouts/AuthLayout";
import ManufactureProcessingDetails from "../components/manufacturer/ManufactureProcessingDetails";
import RediectionToMobile from "../components/RediectionToMobile";
import SelectableFieldSetting from "../components/settings/product-settings";
import {
  ADMIN,
  JEWELLERY_BUYER,
  JEWELLERY_INSPECTOR,
  TAQABETH_ENFORCER,
} from "../constants";

const MusharkaContractsDetails = lazy(
  () => import("../pages/musharka-contracts/MusharkaContractsDetails.page"),
);
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword.pages"));
const Login = lazy(() => import("../pages/auth/Login.pages"));
const Registration = lazy(() => import("../pages/auth/Registration.pages"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword.pages"));
const VerifyOTP = lazy(() => import("../pages/auth/VerifyOTP.pages"));
const Businesses = lazy(() => import("../pages/businesses/Businesses.pages"));
const BusinessesDetails = lazy(
  () => import("../pages/businesses/BusinessesDetails.pages"),
);
const PurchaseRequest = lazy(
  () => import("../pages/purchase-request/PurchaseRequest.pages"),
);
const PurchaseRequestDetails = lazy(
  () => import("../pages/purchase-request/PurchaseRequestDetails.pages"),
);
const GeneralSettings = lazy(
  () => import("../pages/settings/GeneralSettings.pages"),
);
const MetalStoneSettings = lazy(
  () => import("../pages/settings/MetalStoneSetting.pages"),
);
const TaxSettings = lazy(() => import("../pages/settings/TaxSettings.pages"));
const SubAdmins = lazy(() => import("../pages/sub-admins/SubAdmins.pages"));
const SubAdminDetails = lazy(
  () => import("../pages/sub-admins/SubAdminsDetails.pages"),
);
const Transaction = lazy(
  () => import("../pages/transaction/Transaction.pages"),
);
const BankTransfer = lazy(
  () => import("../pages/bank-transfer/BankTransfer.pages"),
);
const TransactionDetail = lazy(
  () => import("../pages/transaction/TransactionDetails.pages"),
);
const UserDetails = lazy(() => import("../pages/users/UserDetails.pages"));
const BankSettings = lazy(() => import("../pages/settings/BankSettings.page"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy.pages"));
const TermsAndConditions = lazy(
  () => import("../pages/TermsAndConditions.pages"),
);
const BankTransferDetails = lazy(
  () => import("../pages/bank-transfer/BankTransferDetails.pages"),
);
const MetalsStonesPools = lazy(
  () => import("../pages/metals-stones-pools/MetalsStonesPools.pages"),
);
const MetalsStonesPoolsDetails = lazy(
  () => import("../pages/metals-stones-pools/MetalsStonesPoolsDetails.pages"),
);
const MusharkaContracts = lazy(
  () => import("../pages/musharka-contracts/MusharkaContracts.pages"),
);
const Manufacturer = lazy(
  () => import("../pages/manufacturer/manufacturer.pages"),
);
const ManufactureDetails = lazy(
  () => import("../pages/manufacturer/ManufactureDetails.pages"),
);
const SubscriptionPlan = lazy(
  () => import("../pages/subscription-plans/SubscriptionPlans.pages"),
);

const InspectionRequests = lazy(
  () => import("../pages/inspector-requests/inspectorRequests.pages"),
);
const ManufactureRequests = lazy(
  () => import("../pages/manufacture-requests/ManufactureRequests.pages"),
);
const StockManagement = lazy(
  () =>
    import("../pages/jewellery-buyer/stock-management/StockManagement.pages"),
);
const DeliveryRequests = lazy(
  () =>
    import("../pages/jewellery-buyer/delivery-requests/DeliveryRequests.pages"),
);
const DeliveryRequestDetails = lazy(
  () =>
    import(
      "../components/jewellery-buyer/delivery-requests/DeliveryRequestDetails"
    ),
);
const RequestManagement = lazy(
  () =>
    import(
      "../pages/taqabeth-enforcer/request-management/requestManagement.pages"
    ),
);
const PurchaseRequestDetail = lazy(
  () =>
    import(
      "../pages/taqabeth-enforcer/request-management/purchaseRequestDetail.pages"
    ),
);
const StocksManagement = lazy(
  () =>
    import("../pages/taqabeth-enforcer/stock-management/stockManagement.pages"),
);

const StockDetails = lazy(
  () =>
    import("../pages/taqabeth-enforcer/stock-management/stockDetails.pages"),
);
const TaqabethProfile = lazy(
  () => import("../pages/taqabeth-enforcer/profile-page/taqabethProfile.pages"),
);
const InspectorProfile = lazy(
  () => import("../pages/inspector-requests/inspectorProfile.pages"),
);

const SubscriptionTransaction = lazy(
  () => import("../pages/transaction/SubscriptionTransaction.pages"),
);

const JewelleryBuyerProfile = lazy(
  () => import("../pages/jewellery-buyer/profile-page/BuyerProfile.pages"),
);

const ProductRequests = lazy(
  () =>
    import("../pages/jewellery-buyer/product-requests/ProductRequests.pages"),
);

const ProductDetails = lazy(
  () => import("../components/jewellery-buyer/stock-management/ProductDetails"),
);

const ProfitDistributionDetails = lazy(
  () => import("../pages/manufacturer/profitDistributions.pages"),
);

const AvailableStockDetails = lazy(
  () =>
    import("../pages/taqabeth-enforcer/stock-management/availableStocks.pages"),
);

const OwnedAssets = lazy(() => import("../components/owned-assets"));

const NotFound = lazy(() => import("../pages/NotFound.pages"));

const publicRoutes = [
  {
    path: "/",
    element: <PublicRoute />,
    children: [
      {
        path: "/",
        element: <AuthLayout />,
        children: [
          { path: "login", element: <Login /> },
          { path: "forgot-password", element: <ForgotPassword /> },
          { path: "reset-password", element: <ResetPassword /> },
          { path: "verify-otp", element: <VerifyOTP /> },
        ],
      },
      { path: "registration", element: <Registration /> },
      { path: "add-card-details", element: <AddPaymentCardDetails /> },
      { path: "add-tap-card-details", element: <AddTapCardDetails /> },
      { path: "privacy-policy", element: <PrivacyPolicy /> },
      { path: "terms-and-conditions", element: <TermsAndConditions /> },
      { path: "*", element: <NotFound /> },

      { path: "onboard-complete", element: <RediectionToMobile /> },
    ],
  },
];

const privateRoutes = [
  {
    path: "/",
    element: <ProtectedRoute allowedRoles={[ADMIN]} />,
    children: [
      {
        path: "/",
        element: <AppLayout userRole={ADMIN} />,
        children: [
          { index: true, element: <Navigate to="/businesses" /> },
          {
            path: "businesses",
            element: <Businesses />,
            children: [
              {
                path: ":businessName",
                element: <BusinessesDetails />,
                children: [{ path: ":username", element: <UserDetails /> }],
              },
            ],
          },
          {
            path: "sub-admins",
            element: <SubAdmins />,
            children: [{ path: ":username", element: <SubAdminDetails /> }],
          },
          {
            path: "transactions",
            element: <Transaction />,
            children: [
              { path: ":transactionId", element: <TransactionDetail /> },
              {
                path: "subscription-transaction/:transactionId",
                element: <SubscriptionTransaction />,
              },
            ],
          },
          {
            path: "purchase-requests",
            element: <PurchaseRequest />,
            children: [
              {
                path: ":purchaseRequestsId",
                element: <PurchaseRequestDetails />,
              },
            ],
          },
          {
            path: "bank-transfer-requests",
            element: <BankTransfer />,
            children: [
              { path: ":bankTransferId", element: <BankTransferDetails /> },
            ],
          },
          {
            path: "metals-stones-pools",
            element: <MetalsStonesPools />,
            children: [
              { path: ":poolId", element: <MetalsStonesPoolsDetails /> },
            ],
          },
          {
            path: "musharka-contracts",
            element: <MusharkaContracts />,
            children: [
              { path: ":musharkaId", element: <MusharkaContractsDetails /> },
            ],
          },
          {
            path: "owned-assets",
            element: <OwnedAssets />,
          },
          {
            path: "manufacturer",
            element: <Manufacturer />,
            children: [
              { path: ":manufactureId", element: <ManufactureDetails /> },
              {
                path: "processing-details/:id",
                element: <ManufactureProcessingDetails />,
              },
              {
                path: "profit-distributions/:profitId",
                element: <ProfitDistributionDetails />,
              },
            ],
          },
          { path: "subscription-plans", element: <SubscriptionPlan /> },
          {
            path: "settings",
            children: [
              { index: true, element: <Navigate to="general-settings" /> },
              { path: "general-settings", element: <GeneralSettings /> },
              { path: "tax-settings", element: <TaxSettings /> },
              { path: "metal-stone-settings", element: <MetalStoneSettings /> },
              {
                path: "selectable-field-settings",
                element: <SelectableFieldSetting />,
              },
              { path: "bank-setting", element: <BankSettings /> },
            ],
          },
        ],
      },
    ],
  },
];

const jewelryInspectorRoutes = [
  {
    path: "/",
    element: <ProtectedRoute allowedRoles={[JEWELLERY_INSPECTOR]} />,
    children: [
      {
        path: "/",
        element: <AppLayout userRole={JEWELLERY_INSPECTOR} />,
        children: [
          { index: true, element: <Navigate to="/inspection-requests" /> },
          {
            path: "inspection-requests",
            element: <InspectionRequests />,
            children: [
              {
                path: ":inspectionId",
                element: <InspectionDetails />,
              },
            ],
          },
          {
            path: "manufacturing-requests",
            element: <ManufactureRequests />,
            children: [
              {
                path: ":inspectionId",
                element: <InspectionDetails />,
              },
            ],
          },
          {
            path: "profiles",
            element: <InspectorProfile />,
          },
        ],
      },
    ],
  },
];

const jewelryBuyerRoutes = [
  {
    path: "/",
    element: <ProtectedRoute allowedRoles={[JEWELLERY_BUYER]} />,
    children: [
      {
        path: "/",
        element: <AppLayout userRole={JEWELLERY_BUYER} />,
        children: [
          { index: true, element: <Navigate to="/stocks-management" /> },
          {
            path: "/stocks-management",
            element: <StockManagement />,
            children: [
              {
                path: ":id",
                element: <ProductDetails />,
              },
            ],
          },
          {
            path: "/product-requests",
            element: <ProductRequests />,
          },
          {
            path: "/delivery-requests",
            element: <DeliveryRequests />,
            children: [{ path: ":id", element: <DeliveryRequestDetails /> }],
          },
          {
            path: "/profile-management",
            element: <JewelleryBuyerProfile />,
          },
        ],
      },
    ],
  },
];

const taqabethEnforcerRoutes = [
  {
    path: "/",
    element: <ProtectedRoute allowedRoles={[TAQABETH_ENFORCER]} />,
    children: [
      {
        path: "/",
        element: <AppLayout userRole={TAQABETH_ENFORCER} />,
        children: [
          { index: true, element: <Navigate to="/dashboard" /> },
          {
            path: "/request-management",
            element: <RequestManagement />,
            children: [
              {
                path: ":taqabethId",
                element: <PurchaseRequestDetail />,
              },
              { path: "pool/:poolId", element: <MetalsStonesPoolsDetails /> },
              {
                path: "musharakah/:musharkaId",
                element: <MusharkaContractsDetails />,
              },
              {
                path: "jewelry-production/:inspectionId",
                element: <InspectionDetails />,
              },
            ],
          },
          {
            path: "/stock-management",
            element: <StocksManagement />,
            children: [
              {
                path: "available-stock/:stockId",
                element: <AvailableStockDetails />,
              },
              {
                path: ":stockId",
                element: <StockDetails />,
              },
            ],
          },
          {
            path: "profile",
            element: <TaqabethProfile />,
          },
        ],
      },
    ],
  },
];

export const routes = [
  ...publicRoutes,
  ...privateRoutes,
  ...jewelryInspectorRoutes,
  ...jewelryBuyerRoutes,
  ...taqabethEnforcerRoutes,
];
